import { useEffect } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts';
import { fetchTotaisImpostosTradicionais } from '../api/TotaisImpostosTradicionaisApi';
import type { FiltrosGestaoImpostos, TotaisImpostosTradicionais } from '../types';

/**
 * ICMS/IPI/PIS/COFINS agregados do período — pagina `Documento/ListaDocumentos`
 * por baixo dos panos, então pode demorar mais que os outros hooks de
 * apuração em períodos com muitos documentos (ver design.md, riscos).
 */
export function useTotaisImpostosTradicionais(
  filtros?: FiltrosGestaoImpostos,
  options?: Omit<UseQueryOptions<TotaisImpostosTradicionais, Error>, 'queryKey' | 'queryFn'>
) {
  const { toast } = useToast();
  const { user } = useAuth();
  const cnpjGestor = user?.cnpj;

  const query = useQuery<TotaisImpostosTradicionais, Error>({
    queryKey: ['gestao-impostos-totais-tradicionais', cnpjGestor, filtros],
    queryFn: () => fetchTotaisImpostosTradicionais(filtros ?? {}),
    staleTime: 5 * 60 * 1000,
    ...options,
    enabled: (options?.enabled ?? true) && !!cnpjGestor,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      variant: 'destructive',
      title: 'Erro ao carregar ICMS/IPI/PIS/COFINS',
      description: 'Não foi possível somar os impostos tradicionais do período. Tente novamente.',
    });
  }, [query.error, toast]);

  return query;
}
